import * as THREE from 'three';

function createRectShape(width, height, centerY = 0) {
  const shape = new THREE.Shape();
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  shape.moveTo(-halfWidth, centerY - halfHeight);
  shape.lineTo(halfWidth, centerY - halfHeight);
  shape.lineTo(halfWidth, centerY + halfHeight);
  shape.lineTo(-halfWidth, centerY + halfHeight);
  shape.lineTo(-halfWidth, centerY - halfHeight);
  return shape;
}

export const DEG2RAD = Math.PI / 180;

export function toVector3(point) {
  if (point && point.isVector3) return point.clone();
  if (Array.isArray(point) && point.length >= 3) return new THREE.Vector3(point[0], point[1], point[2]);
  if (point && typeof point.x === 'number' && typeof point.y === 'number' && typeof point.z === 'number') return new THREE.Vector3(point.x, point.y, point.z);
  return null;
}

export class SampledCurve3 extends THREE.Curve {
  constructor(points) {
    super();
    this.points = points;
    this.distances = [0];

    for (let index = 1; index < points.length; index += 1) {
      this.distances.push(this.distances[index - 1] + points[index - 1].distanceTo(points[index]));
    }

    this.totalLength = this.distances[this.distances.length - 1] || 1;
    this.arcLengthDivisions = THREE.MathUtils.clamp(points.length * 2, 64, 320);
  }

  getPoint(t, target = new THREE.Vector3()) {
    if (!this.points.length) {
      return target.set(0, 0, 0);
    }
    if (this.points.length === 1) {
      return target.copy(this.points[0]);
    }

    const clampedT = THREE.MathUtils.clamp(t, 0, 1);
    const targetDistance = clampedT * this.totalLength;

    for (let index = 1; index < this.distances.length; index += 1) {
      if (targetDistance <= this.distances[index]) {
        const startDistance = this.distances[index - 1];
        const endDistance = this.distances[index];
        const span = endDistance - startDistance || 1;
        const ratio = (targetDistance - startDistance) / span;
        return target.copy(this.points[index - 1]).lerp(this.points[index], ratio);
      }
    }

    return target.copy(this.points[this.points.length - 1]);
  }

  getTangent(t, target = new THREE.Vector3()) {
    if (this.points.length < 2) {
      return target.set(0, 0, 1);
    }

    const clampedT = THREE.MathUtils.clamp(t, 0, 1);
    const targetDistance = clampedT * this.totalLength;

    for (let index = 1; index < this.distances.length; index += 1) {
      if (targetDistance <= this.distances[index]) {
        return target
          .copy(this.points[index])
          .sub(this.points[index - 1])
          .normalize();
      }
    }

    return target
      .copy(this.points[this.points.length - 1])
      .sub(this.points[this.points.length - 2])
      .normalize();
  }
}

export function createCurvedLamellaGeometry({
  points3d,
  points = [],
  baseX = 0,
  baseY = 0,
  baseZ = 0,
  scaleY = 0.001,
  scaleR = 0.01,
  posRAsAngle = true,
  arcRadius = 0.5,
  crossSectionWidth = 0.11,
  crossSectionHeight = 0.11,
  crossSectionCenterY = 0,
  geometryRotationX = -Math.PI / 2,
  exactPath = false,
  lengthAxis = 'x'
}) {
  let curve = null;
  let curvePoints = [];

  if (Array.isArray(points3d) && points3d.length >= 2) {
    curvePoints = points3d.map(toVector3).filter(Boolean);
    if (curvePoints.length < 2) return null;
    curve = exactPath ? new SampledCurve3(curvePoints) : new THREE.CatmullRomCurve3(curvePoints, false, 'centripetal', 0.5);
  } else if (Array.isArray(points) && points.length >= 2) {
    const sorted = [...points].sort((left, right) => (Number(left.posY) || 0) - (Number(right.posY) || 0));
    curvePoints = sorted.map((point) => {
      const posY = Number(point.posY) || 0;
      const posR = Number(point.posR) || 0;
      const along = posY * scaleY;
      const across = posRAsAngle ? arcRadius * Math.sin(posR * DEG2RAD) : posR * scaleR;
      const x = lengthAxis === 'x' ? baseX + along : baseX + across;
      const z = lengthAxis === 'x' ? baseZ + across : baseZ + along;
      return new THREE.Vector3(x, baseY, z);
    });
    curve = new THREE.CatmullRomCurve3(curvePoints, false, 'centripetal', 0.5);
  } else {
    return null;
  }

  const shape = createRectShape(crossSectionWidth, crossSectionHeight, crossSectionCenterY);
  const geometrySteps = THREE.MathUtils.clamp(Math.ceil(curvePoints.length * (exactPath ? 1.2 : 1.8)), 32, 220);
  const geometry = new THREE.ExtrudeGeometry(shape, {
    steps: geometrySteps,
    bevelEnabled: false,
    extrudePath: curve
  });
  geometry.computeVertexNormals();

  if (Number.isFinite(geometryRotationX) && geometryRotationX !== 0) {
    geometry.rotateX(geometryRotationX);
  }

  return geometry;
}
